/**
 * Página de Aguardando Aprovação
 */

import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import {
  HourglassEmptyOutlined,
  WhatsApp,
  EmailOutlined,
  LogoutOutlined
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const PendingApprovalPage: React.FC = () => {
  const { user, signOut, isAdmin, isApproved } = useAuth();
  const navigate = useNavigate();
  const [isCheckingWhatsApp, setIsCheckingWhatsApp] = useState(true);

  // Verificar se usuário tem WhatsApp cadastrado e status de aprovação
  useEffect(() => {
    const checkUserStatus = async () => {
      console.log('🔍 PendingApprovalPage - Iniciando verificação');
      console.log('   user:', user?.email);
      console.log('   isAdmin (contexto):', isAdmin);
      console.log('   isApproved (contexto):', isApproved);
      
      if (!user) {
        console.log('   ❌ Sem usuário - redirecionando para /');
        navigate('/');
        return;
      }

      // Verificar contexto primeiro (mais rápido)
      if (isAdmin || isApproved) {
        console.log('✅ PendingApprovalPage - Contexto indica aprovado/admin');
        console.log('   Redirecionando imediatamente para /app');
        navigate('/app', { replace: true });
        setIsCheckingWhatsApp(false);
        return;
      }

      try {
        console.log('🌐 PendingApprovalPage - Buscando dados do backend...');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/user/${user.uid}`
        );

        if (response.ok) {
          const userData = await response.json();
          
          console.log('📋 PendingApprovalPage - Dados do backend:', {
            email: userData.email,
            whatsapp: userData.whatsapp,
            isAdmin: userData.isAdmin,
            isAdmin_type: typeof userData.isAdmin,
            isApproved: userData.isApproved,
            isApproved_type: typeof userData.isApproved
          });
          
          // Se não tem WhatsApp, redireciona para login onde o modal será exibido
          if (!userData.whatsapp) {
            console.log('⚠️ Usuário sem WhatsApp - redirecionando para login');
            await signOut();
            navigate('/');
            return;
          }
          
          // Se está aprovado ou é admin, redireciona para /app
          const userIsAdmin = userData.isAdmin === 1 || userData.isAdmin === true || userData.isAdmin === '1';
          const userIsApproved = userData.isApproved === 1 || userData.isApproved === true || userData.isApproved === '1';
          
          console.log('🔐 PendingApprovalPage - Verificação de permissões:', {
            userIsAdmin,
            userIsApproved,
            shouldRedirect: userIsAdmin || userIsApproved
          });
          
          if (userIsAdmin || userIsApproved) {
            console.log('✅ Usuário aprovado/admin - REDIRECIONANDO para /app');
            navigate('/app', { replace: true });
            return;
          }
          
          console.log('⏳ Usuário aguardando aprovação - permanecendo na página');
        }
      } catch (error) {
        console.error('❌ Erro ao verificar status do usuário:', error);
      } finally {
        setIsCheckingWhatsApp(false);
      }
    };

    checkUserStatus();
  }, [user, navigate, signOut, isAdmin, isApproved]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  // Mostrar loading enquanto verifica
  if (isCheckingWhatsApp) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        }}
      >
        <Typography sx={{ color: '#FF6B35', fontSize: '1.2rem' }}>
          Verificando...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        p: 3,
      }}
    >
      <Paper
        elevation={24}
        sx={{
          maxWidth: 600,
          width: '100%',
          p: 5,
          textAlign: 'center',
          backgroundColor: '#1a1a1a',
          border: '1px solid rgba(255, 107, 53, 0.2)',
          borderRadius: 3,
        }}
      >
        {/* Ícone */}
        <HourglassEmptyOutlined
          sx={{
            fontSize: 100,
            color: '#FF6B35',
            mb: 3,
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.5 },
            },
          }}
        />

        {/* Título */}
        <Typography
          variant="h4"
          sx={{
            color: '#FFFFFF',
            fontWeight: 700,
            mb: 2,
          }}
        >
          Conta Aguardando Aprovação
        </Typography>

        {/* Mensagem Principal */}
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            mb: 4,
            lineHeight: 1.8,
          }}
        >
          Olá, <strong>{user?.displayName || user?.email}</strong>!
          <br />
          <br />
          Sua conta foi criada com sucesso e está <strong>aguardando aprovação</strong> de um
          administrador.
          <br />
          <br />
          Você receberá acesso ao sistema assim que sua conta for aprovada.
        </Typography>

        {/* Informações de Contato */}
        <Box
          sx={{
            backgroundColor: 'rgba(255, 107, 53, 0.1)',
            border: '1px solid rgba(255, 107, 53, 0.3)',
            borderRadius: 2,
            p: 3,
            mb: 4,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: '#FF6B35',
              fontWeight: 600,
              mb: 2,
            }}
          >
            Entraremos em Contato
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
              <WhatsApp sx={{ color: '#25D366', fontSize: 24 }} />
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Via WhatsApp para confirmação
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
              <EmailOutlined sx={{ color: '#FF6B35', fontSize: 24 }} />
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                {user?.email}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Instruções */}
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 2,
            p: 2,
            mb: 4,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: 1.6,
            }}
          >
            <strong>O que acontece agora?</strong>
            <br />
            1. Nossa equipe receberá uma notificação
            <br />
            2. Entraremos em contato pelo WhatsApp cadastrado
            <br />
            3. Após a confirmação, sua conta será aprovada
            <br />
            4. Você poderá fazer login normalmente
          </Typography>
        </Box>

        {/* Botão de Logout */}
        <Button
          variant="outlined"
          startIcon={<LogoutOutlined />}
          onClick={handleLogout}
          fullWidth
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            py: 1.5,
            '&:hover': {
              borderColor: '#FF6B35',
              backgroundColor: 'rgba(255, 107, 53, 0.1)',
              color: '#FF6B35',
            },
          }}
        >
          Sair
        </Button>

        {/* Tempo Estimado */}
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            mt: 3,
            display: 'block',
          }}
        >
          Tempo estimado de aprovação: até 24 horas úteis
        </Typography>
      </Paper>
    </Box>
  );
};

export default PendingApprovalPage;
